/**
 * Reprodutor das animações do acervo.
 *
 * As 120 animações são arquivos Effekseer (.efkefc), e o runtime que as toca
 * veio junto do acervo — na mesma versão em que foram autoradas. Isso responde
 * a pergunta que estava em aberto no plano: não é preciso converter nada para
 * folhas de sprite.
 *
 * O runtime é WebAssembly e se instala num objeto global, então carrega uma vez
 * por página e é compartilhado por todos os efeitos.
 */

import { ASSETS_BASE } from './manifest.js';

const RUNTIME_JS = `${ASSETS_BASE}/fx/runtime/effekseer.min.js`;
const RUNTIME_WASM = `${ASSETS_BASE}/fx/runtime/effekseer.wasm`;

interface EffekseerContext {
  init(gl: WebGLRenderingContext): void;
  loadEffect(path: string, scale: number, onLoad?: () => void, onError?: (m: string) => void): unknown;
  releaseEffect(effect: unknown): void;
  play(effect: unknown, x: number, y: number, z: number): { setLocation(x: number, y: number, z: number): void };
  update(delta?: number): void;
  draw(): void;
  setProjectionMatrix(m: Float32Array): void;
  setCameraMatrix(m: Float32Array): void;
  stopAll(): void;
}

interface EffekseerGlobal {
  initRuntime(wasmPath: string, onLoad: () => void, onError?: (m: string) => void): void;
  createContext(): EffekseerContext;
  releaseContext(context: EffekseerContext): void;
}

declare global {
  interface Window {
    effekseer?: EffekseerGlobal;
  }
}

let runtimeReady: Promise<EffekseerGlobal> | null = null;

function loadScript(src: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const existing = document.querySelector<HTMLScriptElement>(`script[src="${src}"]`);
    if (existing) return resolve();

    const el = document.createElement('script');
    el.src = src;
    el.async = true;
    el.onload = () => resolve();
    el.onerror = () => reject(new Error(`não foi possível carregar ${src}`));
    document.head.append(el);
  });
}

/** Carrega o runtime uma vez só, por mais que seja pedido. */
export function initEffekseer(): Promise<EffekseerGlobal> {
  runtimeReady ??= loadScript(RUNTIME_JS).then(
    () =>
      new Promise<EffekseerGlobal>((resolve, reject) => {
        const runtime = window.effekseer;
        if (!runtime) return reject(new Error('effekseer.min.js carregou sem expor o global'));
        runtime.initRuntime(
          RUNTIME_WASM,
          () => resolve(runtime),
          (message) => reject(new Error(`runtime do Effekseer falhou: ${message}`)),
        );
      }),
  );
  return runtimeReady;
}

/** Matriz de projeção em perspectiva, coluna a coluna como o WebGL espera. */
function perspective(fovDegrees: number, aspect: number, near: number, far: number): Float32Array {
  const f = 1 / Math.tan((fovDegrees * Math.PI) / 360);
  const range = 1 / (near - far);
  return new Float32Array([
    f / aspect, 0, 0, 0,
    0, f, 0, 0,
    0, 0, (near + far) * range, -1,
    0, 0, near * far * range * 2, 0,
  ]);
}

/** Câmera olhando para a origem, a `distance` de distância. */
function lookAtOrigin(distance: number): Float32Array {
  return new Float32Array([
    1, 0, 0, 0,
    0, 1, 0, 0,
    0, 0, 1, 0,
    0, 0, -distance, 1,
  ]);
}

export interface EffectPlayer {
  /** O canvas onde o reprodutor desenha. */
  readonly canvas: HTMLCanvasElement;
  play(url: string): Promise<void>;
  /** Para o efeito, solta o que estava carregado e apaga o canvas. */
  stop(): void;
  dispose(): void;
  /** Maior número de pixels visíveis num quadro desde o último play(). */
  peakPixels(): number;
}

/** Solta o contexto do Effekseer, mesmo com o runtime já quebrado. */
function releaseContext(runtime: EffekseerGlobal, context: EffekseerContext): void {
  try {
    runtime.releaseContext(context);
  } catch (e) {
    console.warn('Erro ao liberar contexto do Effekseer:', e);
  }
}

/** Solta o contexto WebGL de um canvas, para ele parar de contar no limite. */
function loseWebGL(gl: WebGLRenderingContext): void {
  try {
    gl.getExtension('WEBGL_lose_context')?.loseContext();
  } catch (e) {
    console.warn('Erro ao liberar contexto WebGL:', e);
  }
}

/**
 * Prende um reprodutor a um canvas. O laço de desenho só roda enquanto há algo
 * a mostrar — uma tela de animação parada não deve consumir GPU.
 *
 * Um reprodutor custa caro: um contexto WebGL (o Chrome aceita 16 vivos por
 * página e, passando disso, derruba o mais antigo) e ~19 MB do heap
 * WebAssembly do Effekseer, que o coletor de lixo não alcança. Quem cria um
 * precisa chamar `dispose` — ou reaproveitá-lo, como faz o palco de combate
 * com `stageEffectPlayer`.
 */
export async function createEffectPlayer(
  canvas: HTMLCanvasElement,
  onFrame?: (peakPixels: number) => void,
): Promise<EffectPlayer> {
  const runtime = await initEffekseer();
  const gl = canvas.getContext('webgl', { alpha: true, premultipliedAlpha: true });
  if (!gl) throw new Error('este navegador não deu um contexto WebGL');

  // Daqui em diante o canvas já tem contexto WebGL. Se o Effekseer não subir
  // — com o heap cheio, `init` estoura —, é aqui que o contexto precisa ser
  // solto: órfão, ele ficaria contando no limite até o navegador sacrificar o
  // mais antigo da página, que é o do tabuleiro.
  let created: EffekseerContext | null = null;
  try {
    created = runtime.createContext();
    created.init(gl);
  } catch (e) {
    if (created) releaseContext(runtime, created);
    loseWebGL(gl);
    throw e;
  }
  const context = created;

  let current: unknown = null;
  let frame = 0;
  let running = false;
  let raf = 0;
  let last = 0;
  let peak = 0;
  // Sobe a cada play/stop: um efeito que termina de carregar depois disso
  // chegou tarde e não deve aparecer.
  let generation = 0;

  // Uma amostra pequena e central basta para responder "isto desenhou alguma
  // coisa?" sem o custo de ler a tela inteira a cada quadro. Sem esta medida,
  // um efeito que carrega mas renderiza fora da câmera passaria por bom.
  const sample = { x: canvas.width / 4, y: canvas.height / 4, w: canvas.width / 2, h: canvas.height / 2 };
  const buffer = new Uint8Array(sample.w * sample.h * 4);

  const measure = (): void => {
    gl.readPixels(sample.x, sample.y, sample.w, sample.h, gl.RGBA, gl.UNSIGNED_BYTE, buffer);
    let visible = 0;
    for (let i = 3; i < buffer.length; i += 4) if (buffer[i]! > 8) visible++;
    if (visible > peak) {
      peak = visible;
      onFrame?.(peak);
    }
  };

  const projection = perspective(40, canvas.width / canvas.height, 1, 1000);
  const camera = lookAtOrigin(28);

  const tick = (now: number): void => {
    if (!running) return;
    const delta = last === 0 ? 1 : Math.min((now - last) / 16.666, 4);
    last = now;

    gl.clearColor(0, 0, 0, 0);
    gl.clear(gl.COLOR_BUFFER_BIT);
    context.setProjectionMatrix(projection);
    context.setCameraMatrix(camera);
    context.update(delta);
    context.draw();
    measure();

    // Um efeito do acervo dura no máximo poucos segundos; 8 é folga suficiente
    // para o mais longo e evita um laço que nunca termina.
    if (++frame > 8 * 60) {
      running = false;
      return;
    }
    raf = requestAnimationFrame(tick);
  };

  const halt = (): void => {
    running = false;
    cancelAnimationFrame(raf);
    generation++;
    context.stopAll();
    if (current) context.releaseEffect(current);
    current = null;
  };

  return {
    canvas,
    async play(url: string): Promise<void> {
      const mine = ++generation;
      if (current) {
        context.stopAll();
        context.releaseEffect(current);
        current = null;
      }

      const effect = await new Promise((resolve, reject) => {
        const loaded = context.loadEffect(
          url,
          1.0,
          () => resolve(loaded),
          (message) => reject(new Error(`efeito não carregou: ${message}`)),
        );
      });
      if (mine !== generation) {
        context.releaseEffect(effect);
        return;
      }

      current = effect;
      context.play(current, 0, 0, 0);
      frame = 0;
      last = 0;
      peak = 0;
      if (!running) {
        running = true;
        raf = requestAnimationFrame(tick);
      }
    },
    stop(): void {
      halt();
      // O canvas guarda o último quadro desenhado: um reprodutor reaproveitado
      // abriria o combate seguinte com o golpe do anterior congelado na tela.
      gl.clearColor(0, 0, 0, 0);
      gl.clear(gl.COLOR_BUFFER_BIT);
    },
    dispose(): void {
      try {
        halt();
      } catch {
        // Runtime já quebrado: o que importa agora é soltar os contextos.
      }
      releaseContext(runtime, context);
      loseWebGL(gl);
    },
    peakPixels: () => peak,
  };
}

/** Os dois lados do palco de combate. */
export type StageSide = 'atacante' | 'defensor';

/** Lado do canvas de efeito do palco, em pixels; o CSS o fixa no mesmo tamanho. */
const STAGE_FX_SIZE = 360;

const stagePlayers = new Map<StageSide, Promise<EffectPlayer>>();

/**
 * O reprodutor de um lado do palco de combate — o mesmo a página inteira.
 *
 * Cada combate criava dois reprodutores e os desfazia no fim. Mesmo desfeito
 * direito, o runtime guarda numa tabela interna todo contexto que já registrou,
 * e numa partida de turnos sem fim isso também cresce sem fim. Com os dois
 * reprodutores fixos, um combate não custa nada: nenhum contexto WebGL novo,
 * nenhuma alocação no heap WebAssembly. A cena só pendura o canvas e o solta.
 *
 * Se o navegador derrubar o contexto de um deles (GPU reiniciada, por
 * exemplo), ele é descartado e o próximo combate monta outro.
 */
export function stageEffectPlayer(side: StageSide): Promise<EffectPlayer> {
  const existing = stagePlayers.get(side);
  if (existing) return existing;

  const player = (async () => {
    const canvas = document.createElement('canvas');
    canvas.width = STAGE_FX_SIZE;
    canvas.height = STAGE_FX_SIZE;
    canvas.className = 'combat-fx-canvas';

    const fx = await createEffectPlayer(canvas);
    canvas.addEventListener(
      'webglcontextlost',
      () => {
        if (stagePlayers.get(side) === player) stagePlayers.delete(side);
        fx.dispose();
      },
      { once: true },
    );
    return fx;
  })();

  stagePlayers.set(side, player);
  // Não subiu: a próxima cena tenta de novo em vez de herdar o erro.
  player.catch(() => {
    if (stagePlayers.get(side) === player) stagePlayers.delete(side);
  });
  return player;
}
